"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";

type LogoProps = {
  className?: string;
  href?: string;
  ariaLabel?: string;
  variant?: "lockup" | "mark";
  /** Height in CSS px. Lockup proportions: ~4.75:1 (w:h). Mark: ~1:1. */
  height?: number;
};

const LOCKUP_RATIO = 2048 / 431;
const MARK_RATIO = 1; // figurative mark renders square (trimmed bbox)

export function Logo({
  className,
  href = "/",
  ariaLabel = "ClarusPlus Startseite",
  variant = "lockup",
  height = 36,
}: LogoProps) {
  const isMark = variant === "mark";
  const src = isMark ? "/brand/logo-mark.png" : "/brand/logo.png";
  const ratio = isMark ? MARK_RATIO : LOCKUP_RATIO;
  const width = Math.round(height * ratio);
  const intrinsicWidth = isMark ? 1518 : 2048;
  const intrinsicHeight = isMark ? 1486 : 431;

  const pathname = usePathname();

  // When already on the link target, Next.js Link does nothing visible — the
  // user clicks the logo expecting *something* (typically scroll-to-top).
  // Intercept and smooth-scroll up. On any other path, fall through to the
  // normal navigation.
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      pathname === href &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      event.button === 0
    ) {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <Link
      href={href}
      aria-label={ariaLabel}
      onClick={handleClick}
      className={cn(
        "inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2",
        className,
      )}
    >
      <Image
        src={src}
        alt=""
        width={intrinsicWidth}
        height={intrinsicHeight}
        priority
        sizes={`${width}px`}
        style={{ width, height: "auto" }}
        className="select-none"
      />
    </Link>
  );
}
